// Two-way calendar-mirror reconcile decision engine (pure, total, isomorphic).
// Implements: calendar-sync.md v2.0 (sections 3, 4, 5; invariants 1, 4, 5, 6).
//
// reconcileDecide is the heart of the mirror. Given ONE direction (from → to),
// the COMPLETE windowed snapshot read off `from`, the direction's durable link
// map already written to `to`, and the reconcile window, it returns the
// ascending, deterministic set of create/update/delete Actions the caller must
// apply. It is a pure function: no I/O, no Date.now, no Math.random. Equal
// inputs always yield equal outputs, so the reconcile self-heals drift and a
// second pass over the post-apply state is a no-op (idempotency, invariant 2).
//
// SNAPSHOT PRECONDITION: `nativeChanges` must be the complete current state of
// the source calendar over the window (a full snapshot read, never an
// incremental feed). The delete diff below (links minus survivors) is only
// correct under that precondition; the readWindow port guarantees it by
// construction.
//
// The filters, in order, realise the spec invariants:
//   (a) ECHO   — drop any change carrying a mirror tag (isMirror). A mirror can
//                never be mirrored back, so two-way is loop-free (invariant 1).
//   (b) STATUS — mirror only busy / tentative / out-of-office; skip free and
//                anything the host declined (invariant 5). All-day entries get
//                no special treatment: an informational all-day entry arrives as
//                'free' from both adapters and dies here, while an all-day
//                busy/oof commitment (a vacation week) mirrors as an ALL-DAY
//                event rendered in the host's zone.
//   (c) WINDOW — keep only occurrences whose half-open [start, end) intersects
//                the half-open reconcile window [window.start, window.end): at
//                window.start IN, at window.end OUT (invariant 6, uses the shared
//                half-open `overlaps` from intervals.ts). The window reaches
//                retentionDays into the PAST, so an ended-but-retained
//                occurrence still survives; once it falls behind the retention
//                tail it drops out and the diff below reaps its mirror — that IS
//                the retention deletion (no separate mechanism).
//   (d) DIFF   — key surviving natives against the existing link map by
//                occurrenceKey: CREATE when no link, UPDATE when the render hash
//                moved, DELETE when a link's occurrence is gone from the
//                surviving set OR the native was cancelled / declined
//                (invariant 4 frees the slot).
//
// CONTRACT: `existingLinks` is THIS direction's link map (the per-direction
// MirrorLinksDoc's `links`); reconcileDecide keys purely on occurrenceKey and
// never sees the sibling direction's doc. `off` mode and a degenerate window
// both yield [].
import { overlaps } from './intervals.js';
import { mirrorRenderHash } from './index.js';
import { allDayDatesInZone } from './time.js';
/** Epoch milliseconds for an Instant. Total over valid ISO-8601 UTC strings. */
function ms(instant) {
    return Date.parse(instant);
}
/**
 * Decide the reconcile actions for a single direction. Pure and deterministic:
 * the output is sorted by occurrenceKey and depends only on the arguments.
 * `timezone` is the HOST's IANA zone (HostConfig.timezone): the render zone for
 * all-day mirrors, and thereby an input to the render hash. Returns [] on
 * degenerate input (mode 'off', or an empty/inverted window).
 */
export function reconcileDecide(args) {
    const { direction, nativeChanges, existingLinks, window, timezone } = args;
    // Degenerate input → no actions. An 'off' direction mirrors nothing; an empty
    // or inverted window carries no instants (guarding this is essential: without
    // it every link would look orphaned and be deleted).
    if (direction.mode === 'off')
        return [];
    if (!(ms(window.start) < ms(window.end)))
        return [];
    // (a)+(b)+(c): bucket the native changes.
    //   survivors — occurrences to mirror (create/update candidates), by key.
    //   removals  — occurrences the host explicitly cancelled or declined, by key;
    //               these free the slot (a DELETE when a link exists, invariant 4).
    const survivors = new Map();
    const removals = new Map();
    for (const nc of nativeChanges) {
        // (a) ECHO suppression (invariant 1): never re-mirror a mirror.
        if (nc.isMirror)
            continue;
        const key = nc.occurrenceKey;
        // Cancelled or declined → the mirror must be removed, not written. Recorded
        // as a removal so a delete carries the source native for context.
        if (nc.isCancelled || nc.responseStatus === 'declined') {
            removals.set(key, nc);
            continue;
        }
        // (b) STATUS filter (invariant 5): keep ONLY busy / tentative / oof. The
        // positive whitelist rejects 'free', which is also what implements the
        // "skip all-day informational entries" clause: both adapters deliver an
        // informational (transparent / free) all-day entry as status 'free', while
        // an all-day busy/oof commitment survives here and mirrors as an ALL-DAY
        // event (finding 2 — a blanket isAllDay skip would drop vacations).
        if (!(nc.status === 'busy' || nc.status === 'tentative' || nc.status === 'oof')) {
            continue;
        }
        // (c) WINDOW filter (invariant 6): half-open intersection with the window.
        if (!overlaps({ start: nc.start, end: nc.end }, { start: window.start, end: window.end })) {
            continue;
        }
        survivors.set(key, nc);
    }
    // A removal always wins over a survivor for the same occurrence (a decline
    // after an accept frees the slot even if a stale busy change also arrived).
    for (const key of removals.keys())
        survivors.delete(key);
    const actions = [];
    // (d) CREATE / UPDATE over the surviving occurrences. The update gate hashes
    // the RENDERED payload, so both a native edit and a direction re-render
    // (mode/label/colour/category flip) move the hash and fire the patch.
    for (const [key, nc] of survivors) {
        const link = existingLinks[key];
        const payload = toMirrorEvent(nc, direction, timezone);
        if (!link) {
            // No link yet → create the mirror.
            actions.push({
                kind: 'create',
                direction,
                occurrenceKey: key,
                native: nc,
                payload,
            });
        }
        else if (mirrorRenderHash(payload) !== link.renderHash) {
            // Link exists and the render hash moved → patch the mirror.
            actions.push({
                kind: 'update',
                direction,
                occurrenceKey: key,
                native: nc,
                link,
                payload,
            });
        }
        // Link exists and hashes match → no-op (idempotent).
    }
    // (d) DELETE: every link whose occurrence no longer survives — a vanished,
    // moved, or retention-expired occurrence (orphan, native absent) or an
    // explicit cancel / decline (native carried for context). Set difference:
    // links − survivors. Correct because nativeChanges is a complete snapshot.
    for (const [key, link] of Object.entries(existingLinks)) {
        if (survivors.has(key))
            continue;
        const removedBy = removals.get(key);
        actions.push({
            kind: 'delete',
            direction,
            occurrenceKey: key,
            link,
            ...(removedBy ? { native: removedBy } : {}),
        });
    }
    // Deterministic order: ascending by occurrenceKey. Each key yields at most one
    // action (create/update keys and delete keys are disjoint), so no tie-break is
    // needed.
    actions.sort((a, b) => a.occurrenceKey < b.occurrenceKey ? -1 : a.occurrenceKey > b.occurrenceKey ? 1 : 0);
    return actions;
}
/**
 * Render the mirror NewEvent for a native occurrence under a direction. Both
 * modes produce a PRIVATE, no-attendee, reminder-free block that never mints an
 * online meeting (booking.ts shape, calendar-sync.md section 6):
 *
 *   busy — a fixed opaque label (direction.label || 'Busy'), no body, no
 *          location, no join link.
 *   full — the real subject/body/location too, and the native join link copied
 *          as TEXT into the body (and, absent a real location, into location);
 *          NEVER as a native onlineMeeting (section 8: no second Teams meeting).
 *
 * PRIVACY DEGRADE (section 8): a native the host flagged private or
 * confidential (NativeChange.privacy) renders as the BUSY payload even on a
 * `full` direction — busy label only, no subject/body/location/joinUrl — so
 * flagged content never crosses the tenant boundary. Because the stored link
 * hash is the RENDER hash, a flag appearing on an already-mirrored native moves
 * the hash and the next pass PATCHes the existing full mirror down to the busy
 * shape (retroactive scrub, same mechanism as a full → busy mode flip). The
 * source flag is NOT the mirror's own visibility: every mirror stays
 * visibility 'private' in both modes regardless of this flag.
 *
 * The clearable fields (description, location) are ALWAYS present, as the empty
 * string when there is nothing to carry, so an update PATCH actively RETRACTS
 * content the native no longer has (or that a full → busy flip must scrub) —
 * both mappers skip undefined fields, so an omitted field would leave stale
 * detail on the mirror forever (finding 12).
 *
 * An all-day native mirrors as an ALL-DAY event: `allDay` carries the local
 * calendar days of the native's span rendered in `timezone` (the HOST's IANA
 * zone), so a Zurich vacation week lands on the same calendar days (finding 2).
 *
 * Styling is per target platform: a Google target (`to` = scandit-gmail) carries
 * googleColorId (default '11', Tomato); an M365 target (`to` = numaco-m365)
 * carries outlookCategory (default 'Scandit'). The correlation tag stamps origin
 * = direction.from with a deterministic, pure correlationId so the mirror is
 * recognised and skipped on the sibling pass (echo suppression, invariant 1).
 * `remindersOff` is stamped on every payload; the mappers render it as Google
 * `reminders: {useDefault:false, overrides:[]}` / Graph `isReminderOn: false`
 * (section 3 payload table, finding 11).
 */
export function toMirrorEvent(native, direction, timezone) {
    // Deterministic, pure correlation id (no Date.now / Math.random): the origin
    // connection plus the occurrence key uniquely and stably identifies the mirror.
    const correlationId = `${direction.from}|${native.occurrenceKey}`;
    const correlationTag = { origin: direction.from, correlationId };
    const styling = targetStyling(direction);
    // All-day rendering (invariant 5): the native's span converted into the
    // host's local calendar days, endDate exclusive. Omitted for timed natives.
    const allDay = native.isAllDay
        ? { ...allDayDatesInZone(native.start, native.end, timezone), timeZone: timezone }
        : undefined;
    // full renders the real content ONLY for an unflagged native: a private or
    // confidential flag degrades the render to the busy shape below (section 8).
    if (direction.mode === 'full' && native.privacy === undefined) {
        const title = native.subject || direction.label || 'Busy';
        const lines = [];
        if (native.body)
            lines.push(native.body);
        // The join link is copied as TEXT only — never a native onlineMeeting.
        if (native.joinUrl)
            lines.push(`Join: ${native.joinUrl}`);
        // '' when there is nothing to carry: an explicit empty, never undefined,
        // so a PATCH retracts a body/location the native cleared.
        const description = lines.join('\n\n');
        const location = native.location ?? native.joinUrl ?? '';
        return {
            title,
            start: native.start,
            end: native.end,
            attendees: [],
            visibility: 'private',
            description,
            location,
            correlationTag,
            remindersOff: true,
            ...(allDay !== undefined ? { allDay } : {}),
            ...styling,
        };
    }
    // busy (and any non-full) mode, plus a privacy-flagged native on a full
    // direction: opaque private block, fixed label, no detail. description /
    // location are EXPLICIT empties so a full → busy flip (or a flag appearing on
    // a full-mirrored native) scrubs the previously copied content (finding 12).
    return {
        title: direction.label || 'Busy',
        start: native.start,
        end: native.end,
        attendees: [],
        visibility: 'private',
        description: '',
        location: '',
        correlationTag,
        remindersOff: true,
        ...(allDay !== undefined ? { allDay } : {}),
        ...styling,
    };
}
/**
 * Per-target styling for the mirror. A Google target carries googleColorId; an
 * M365 target carries outlookCategory. The default colour '11' (Tomato) and the
 * default category 'Scandit' are the section-3 defaults; a direction may override
 * either.
 */
function targetStyling(direction) {
    if (direction.to === 'scandit-gmail') {
        return { googleColorId: direction.googleColorId || '11' };
    }
    return { outlookCategory: direction.outlookCategory || 'Scandit' };
}
/**
 * Default sync posture for a newly enabled pair (calendar-sync.md section 4):
 * BOTH directions in `busy` mode. `full` is never a default a host inherits — it
 * is always a deliberate per-direction opt-in (section 8), so this helper never
 * produces it.
 */
export function enableSyncDefaults(a, b) {
    return [
        { from: a, to: b, mode: 'busy' },
        { from: b, to: a, mode: 'busy' },
    ];
}
// ===========================================================================
// The reconcile ORCHESTRATOR (impure section: all I/O injected as ports; still
// NO Date.now / Math.random — every instant comes from deps.now()).
// Implements: calendar-sync.md v2.0 (sections 5, 7; invariants 1, 2, 4).
//
// Modelled on booking.ts confirmBooking, one saga per direction:
//   LEASE   — acquire the per-(hostId, direction.from) lease (the concurrency
//             analogue of confirmBooking's acquireHold): a LIVE holder means a
//             pass is already mirroring this source, so we SKIP (no double run).
//             acquireLease returns the record it read, saving a separate get.
//   READ    — SNAPSHOT-read the complete windowed state from the source
//             (readWindow), and the target's mirror-tagged event index
//             (readMirrorIndex, the in-band adoption read). The window is
//             [now − retentionDays, now + rollingWindowDays).
//   DECIDE  — the pure reconcileDecide, over THIS direction's link map only.
//   ADOPT   — a create whose occurrence already has an untracked tagged mirror
//             on the target ADOPTS that mirror instead of creating a duplicate
//             (invariant 2's store-loss fallback): the create is CONVERTED into
//             an update that PATCHes the adopted mirror to the current render
//             (its content may predate a native edit) and links it only after
//             the patch lands. Tagged unlinked events with no surviving native
//             are orphans and are deleted.
//   APPLY   — create/update/delete the mirror through the TARGET provider; a
//             delete uses the PLAIN, non-notifying deleteMirrorEvent (mirrors
//             have no attendees, invariant 4), NOT a booking cancel. Capped at
//             MAX_APPLY_ACTIONS_PER_PASS so a bootstrap burst is chunked across
//             ticks. Per-action failure isolation: a deterministic client
//             rejection (400/404/410/413/422) skips JUST that action and
//             continues (a poison payload can never wedge the direction); a
//             transient failure (429 / 5xx / timeout / network) stops issuing
//             writes for the pass — the next tick converges.
//   PERSIST — the pass holds the whole link map in memory and writes the
//             direction doc ONCE at pass end when dirty (plus an interim flush
//             every LINKS_FLUSH_EVERY mutations to bound loss on a crash; the
//             adoption read makes any lost tail safe). Before the final write
//             the pass re-asserts the lease; a stolen lease aborts the write.
//   FINISH  — finishPass (release + health stamp) in a finally, always: a
//             completed pass stamps lastSuccessInstant, a thrown pass the
//             stage-prefixed error, in the SAME write that drops the lease.
//
// Two independent pipes (one per direction, keyed on direction.from): a source
// event is only ever read from `from` and written to `to`, never back to `from`.
// ===========================================================================
/** Milliseconds in a rolling-window day. */
const MS_PER_DAY = 24 * 60 * 60_000;
/**
 * Default retention for ended mirrors (calendar-sync.md section 8, retention
 * policy): the reconcile window reaches this many days into the past, so a
 * mirror survives its occurrence's end by this long before the windowed diff
 * reaps it. Overridable per host via HostConfig.sync.retentionDays.
 */
export const DEFAULT_RETENTION_DAYS = 5;
/**
 * Hard cap on provider write actions per pass. Bounds a bootstrap burst (a first
 * enable of a ~200-link direction) to a chunk per tick, so the pass stays well
 * inside its lease and never trips provider write limiters; the remainder
 * converges over the next ticks (reconcileDecide is deterministic, so the same
 * leading chunk never re-applies).
 */
export const MAX_APPLY_ACTIONS_PER_PASS = 150;
/**
 * Interim links-doc flush cadence: persist the in-memory map every this many
 * mutations, bounding how much link state a crash can lose. The adoption read
 * makes any lost tail safe (an untracked mirror is adopted next pass), so this
 * is a cost bound, not a correctness requirement.
 */
const LINKS_FLUSH_EVERY = 20;
/**
 * Lease lifetime. Comfortably longer than one reconcile pass (the scheduler runs
 * on a 10 minute cadence, section 7) so a live pass is never pre-empted, yet
 * bounded so a crashed pass's lease self-heals within a couple of cycles.
 */
const LEASE_TTL_MS = 5 * 60_000;
/**
 * Heartbeat cadence for the lease. The apply loop renews the lease once more than
 * this much wall time has elapsed since the lease was granted or last extended,
 * so a pass of ANY length keeps its lease and is never pre-empted mid-apply — and
 * a pass whose read phase stalled past the TTL discovers the loss BEFORE its
 * first write. A normal pass (a few bounded HTTP calls) never reaches this, so
 * the heartbeat costs nothing in steady state.
 */
const LEASE_HEARTBEAT_MS = LEASE_TTL_MS / 2;
/**
 * HTTP statuses that mark an apply action as DETERMINISTICALLY rejected by the
 * provider (a poison payload or a vanished target): retrying the identical
 * request can never succeed, so the action is skipped and the pass continues.
 * Everything else (429, 5xx, timeout 408, network 0, unknown) is transient and
 * aborts the pass's writes instead.
 */
const POISON_STATUSES = new Set([400, 404, 410, 413, 422]);
/** Epoch milliseconds → ISO-8601 UTC Instant. */
function instantFromMs(epochMs) {
    return new Date(epochMs).toISOString();
}
/**
 * The HTTP status carried on a provider error, when there is one. The calendar
 * package's CalendarApiError carries a numeric `status`; core stays decoupled by
 * duck-typing it rather than importing the class (calendar depends on core, not
 * the reverse).
 */
function statusOf(err) {
    const status = err?.status;
    return typeof status === 'number' ? status : undefined;
}
export class ReconcileError extends Error {
    code;
    cause;
    constructor(code, message, cause) {
        super(message);
        this.name = 'ReconcileError';
        this.code = code;
        if (cause !== undefined)
            this.cause = cause;
    }
}
/**
 * Build the durable link entry for a mirrored occurrence — the map value the
 * next pass reads back: `mirrorId`, the `renderHash` update gate (the hash of
 * the RENDERED payload just written, so an unchanged native re-rendered under
 * the same direction hashes identically and does not spuriously update),
 * the correlation id, and the series/occurrence identity.
 */
function buildLinkEntry(args) {
    const { direction, native, payload, mirrorId, now } = args;
    return {
        nativeId: native.nativeId,
        mirrorId,
        renderHash: mirrorRenderHash(payload),
        correlationId: `${direction.from}|${native.occurrenceKey}`,
        lastReconciledInstant: now,
        ...(native.seriesId !== undefined ? { nativeSeriesId: native.seriesId } : {}),
        ...(native.occurrenceInstant !== undefined
            ? { occurrenceInstant: native.occurrenceInstant }
            : {}),
    };
}
/**
 * Run one reconcile pass for a single direction. Acquire the lease (skip if a
 * live holder owns it), snapshot-read the source window and the target's tagged
 * mirror index, decide via the pure engine, adopt untracked mirrors, apply each
 * remaining action through the target with per-action failure isolation and the
 * per-pass cap, persist the direction's link doc, and finish (release + health)
 * in a finally. See the module header for the full contract. Never throws on a
 * per-action apply failure (isolation + convergence); throws a typed
 * ReconcileError only when a read or something unexpected fails.
 */
export async function runReconcile(deps, hostId, direction) {
    const source = direction.from;
    // PASS-UNIQUE holder: the injected holderId disambiguates concurrent passes over
    // the same (hostId, source), so renewLease's holder check can actually detect a
    // stolen lease (see ReconcileDeps.holderId).
    const holder = `reconcile|${hostId}|${source}|${deps.holderId}`;
    const nowInstant = deps.now();
    const log = deps.logger ?? ((msg, err) => console.error(msg, err));
    const skipped = {
        hostId,
        direction,
        ran: false,
        created: 0,
        updated: 0,
        deleted: 0,
        adopted: 0,
        failed: 0,
        deferred: 0,
    };
    // 1. LEASE — the concurrency guard (the reconcile analogue of acquireHold). A
    //    live holder on (hostId, direction.from) means another pass is already
    //    mirroring this source, so we skip rather than double-run. The store hands
    //    back the record it read (subscription state for the sweep's renewal step),
    //    saving a separate get.
    const leaseRecord = await deps.syncState.acquireLease(hostId, source, holder, LEASE_TTL_MS, nowInstant);
    if (leaseRecord === null)
        return skipped;
    // The health outcome finishPass merges into the SAME write that releases the
    // lease (zero extra writes, section 7): a completed pass stamps success (the
    // store resets consecutiveFailures), a thrown pass stamps the stage-prefixed
    // error (the store extends the streak). A skipped pass never gets here, and a
    // lease-lost pass's stamp no-ops (finishPass is holder-scoped).
    let outcome = {};
    try {
        // 2. WINDOW — [now − retentionDays, now + rollingWindowDays), half-open. The
        //    retention tail keeps ended mirrors alive for retentionDays; the same
        //    window bounds the provider reads AND the engine filter, so the delete
        //    diff and the snapshot agree on what "in window" means.
        const retentionDays = deps.config.sync?.retentionDays ?? DEFAULT_RETENTION_DAYS;
        const window = {
            start: instantFromMs(ms(nowInstant) - retentionDays * MS_PER_DAY),
            end: instantFromMs(ms(nowInstant) + deps.config.rollingWindowDays * MS_PER_DAY),
        };
        // 3. READ — the complete windowed snapshot from the source (the delete
        //    diff's precondition), and the target's mirror-tagged event index (the
        //    in-band adoption read, invariant 2).
        const provider = deps.resolveProvider(source);
        let read;
        try {
            read = await provider.readWindow({ window });
        }
        catch (e) {
            throw new ReconcileError('SourceReadFailed', `readWindow failed for ${source}`, e);
        }
        const target = deps.resolveProvider(direction.to);
        let mirrorIndex;
        try {
            mirrorIndex = await target.readMirrorIndex({ window });
        }
        catch (e) {
            // Without the index, a create could duplicate an untracked mirror — the
            // exact class adoption exists to prevent. Abort; the next tick retries.
            throw new ReconcileError('TargetReadFailed', `readMirrorIndex failed for ${direction.to}`, e);
        }
        // 4. LINKS — one doc read; the pass holds the whole map in memory and writes
        //    it back only when dirty.
        const doc = await deps.links.getDirection(hostId, direction.from, direction.to);
        const linkMap = { ...(doc?.links ?? {}) };
        let dirty = false;
        let sinceFlush = 0;
        const flushLinks = async () => {
            await deps.links.putDirection({
                hostId,
                direction: { from: direction.from, to: direction.to },
                updatedInstant: deps.now(),
                links: linkMap,
            });
            sinceFlush = 0;
        };
        // 5. DECIDE (pure), over THIS direction's link map only (pipe isolation is
        //    structural now: the sibling direction lives in its own doc).
        const actions = reconcileDecide({
            direction,
            nativeChanges: read.changes,
            existingLinks: linkMap,
            window,
            // The host's zone renders all-day mirrors (and is folded into the render
            // hash through the payload).
            timezone: deps.config.timezone,
        });
        // 6. ADOPT (invariant 2, the store-loss / crash-orphan fallback). Index the
        //    target's tagged events that belong to THIS direction (origin match) and
        //    are not the tracked mirror of any link. A create whose occurrence has
        //    such a candidate adopts it (an update PATCHing it to the current
        //    render, then the link — no duplicate create); whatever candidates
        //    remain un-adopted are orphans and are torn down below.
        //    Foreign origins (the sibling pipe read as a source elsewhere, or the
        //    booking saga's 'booking' mirrors) are never adopted and never deleted.
        const correlationPrefix = `${direction.from}|`;
        const linkedMirrorIds = new Set(Object.values(linkMap).map((l) => l.mirrorId));
        const unlinkedTagged = new Map(); // occurrenceKey → candidate eventIds
        for (const entry of mirrorIndex) {
            if (entry.origin !== direction.from)
                continue;
            if (!entry.correlationId.startsWith(correlationPrefix))
                continue;
            if (linkedMirrorIds.has(entry.eventId))
                continue;
            const key = entry.correlationId.slice(correlationPrefix.length);
            const list = unlinkedTagged.get(key) ?? [];
            list.push(entry.eventId);
            unlinkedTagged.set(key, list);
        }
        // Deterministic candidate order (the index read's order is provider-defined).
        for (const list of unlinkedTagged.values())
            list.sort();
        let adopted = 0;
        const applyActions = [];
        for (const action of actions) {
            if (action.kind === 'create') {
                const candidates = unlinkedTagged.get(action.occurrenceKey);
                const candidate = candidates?.shift();
                if (candidate !== undefined) {
                    // Adopt: PATCH the existing tagged mirror to the CURRENT render
                    // instead of creating a twin. The candidate may carry content
                    // rendered from an OLDER native state (it was created by a pass that
                    // crashed or lost its lease before persisting the link, and the
                    // native may have moved since), so linking it under the current
                    // render hash WITHOUT patching would freeze that stale content
                    // forever — the stored hash would claim it settled. The create is
                    // therefore CONVERTED into an update against the candidate and flows
                    // through the apply loop like any other action: the link (with its
                    // now-verified render hash) is written only after the PATCH lands,
                    // and a deferred or failed adoption simply re-adopts next pass (the
                    // candidate is still unlinked in the index).
                    applyActions.push({
                        kind: 'update',
                        direction,
                        occurrenceKey: action.occurrenceKey,
                        native: action.native,
                        link: buildLinkEntry({
                            direction,
                            native: action.native,
                            payload: action.payload,
                            mirrorId: candidate,
                            now: nowInstant,
                        }),
                        payload: action.payload,
                    });
                    adopted++;
                    continue;
                }
            }
            applyActions.push(action);
        }
        // Un-adopted tagged leftovers are orphans (no surviving native, or a
        // duplicate beside a tracked mirror): tear them down, deterministically.
        const orphanIds = [...unlinkedTagged.values()].flat().sort();
        // 7. APPLY through the TARGET, capped and failure-isolated. The lease is
        //    HEARTBEAT-renewed (renewLease) once more than LEASE_HEARTBEAT_MS has
        //    elapsed since the grant or the last extension — which also catches a
        //    lease that lapsed DURING the read phase BEFORE the first write. If a
        //    heartbeat finds the lease lost (another holder owns it), the pass stops
        //    writing and ends cleanly: the new holder is authoritative.
        const work = [
            ...applyActions.map((action) => ({ kind: 'action', action })),
            ...orphanIds.map((eventId) => ({ kind: 'orphan', eventId })),
        ];
        let created = 0;
        let updated = 0;
        let deleted = 0;
        let failed = 0;
        let deferred = 0;
        let leaseLost = false;
        let abortApply = false;
        let abortMessage;
        let lastRenewMs = ms(nowInstant);
        for (let i = 0; i < work.length; i++) {
            if (leaseLost || abortApply)
                break;
            // CAP: chunk a burst across ticks; the tail is deferred, not failed.
            if (i >= MAX_APPLY_ACTIONS_PER_PASS) {
                deferred = work.length - i;
                break;
            }
            const tick = deps.now();
            // BUDGET (webhook path only): once the hard apply budget has elapsed since
            // pass start, stop issuing writes — exactly like the lease-lost check —
            // and defer the tail. What was applied is persisted below; the receiver
            // acks fast and the next poll tick converges the rest (finding 13).
            if (deps.applyBudgetMs !== undefined && ms(tick) - ms(nowInstant) >= deps.applyBudgetMs) {
                deferred = work.length - i;
                break;
            }
            // HEARTBEAT: once more than half the TTL has elapsed since the last
            // extension, push the lease forward so this pass keeps it for any length.
            if (ms(tick) - lastRenewMs >= LEASE_HEARTBEAT_MS) {
                const renewed = await deps.syncState.renewLease(hostId, source, holder, LEASE_TTL_MS, tick);
                if (!renewed) {
                    // Another holder owns the lease now: stop writing, end the pass
                    // cleanly (the final links write below is skipped too — lease-holder-
                    // only writes; an untracked mirror this pass created is adopted by the
                    // new holder's next pass).
                    leaseLost = true;
                    break;
                }
                lastRenewMs = ms(tick);
            }
            const w = work[i];
            try {
                if (w.kind === 'orphan') {
                    // A tagged mirror with no link and no surviving native: tear it down
                    // via the PLAIN, non-notifying teardown (invariant 4).
                    await target.deleteMirrorEvent({ providerId: direction.to, eventId: w.eventId });
                    deleted++;
                }
                else {
                    const action = w.action;
                    if (action.kind === 'create') {
                        const native = action.native;
                        const payload = action.payload;
                        const ref = await target.createEvent(payload);
                        // Record the link in the in-memory map ONLY after the mirror exists;
                        // a failed create threw above, so no dangling entry is ever written.
                        linkMap[action.occurrenceKey] = buildLinkEntry({
                            direction,
                            native,
                            payload,
                            mirrorId: ref.eventId,
                            now: nowInstant,
                        });
                        dirty = true;
                        sinceFlush++;
                        created++;
                    }
                    else if (action.kind === 'update') {
                        const native = action.native;
                        const link = action.link;
                        const ref = { providerId: direction.to, eventId: link.mirrorId };
                        await target.updateEvent(ref, action.payload);
                        // Patch the entry's hash/stamp in place so the next pass sees a
                        // settled state (same mirrorId).
                        linkMap[action.occurrenceKey] = buildLinkEntry({
                            direction,
                            native,
                            payload: action.payload,
                            mirrorId: link.mirrorId,
                            now: nowInstant,
                        });
                        dirty = true;
                        sinceFlush++;
                        updated++;
                    }
                    else {
                        const link = action.link;
                        const ref = { providerId: direction.to, eventId: link.mirrorId };
                        // PLAIN, non-notifying teardown (invariant 4), NOT a booking cancel.
                        await target.deleteMirrorEvent(ref);
                        delete linkMap[action.occurrenceKey];
                        dirty = true;
                        sinceFlush++;
                        deleted++;
                    }
                }
            }
            catch (err) {
                failed++;
                const status = statusOf(err);
                const what = w.kind === 'orphan' ? `orphan ${w.eventId}` : `${w.action.kind} ${w.action.occurrenceKey}`;
                if (status !== undefined && POISON_STATUSES.has(status)) {
                    // Deterministic client rejection: retrying the identical request can
                    // never succeed. Skip JUST this action and continue — a poison payload
                    // must never wedge the whole direction (the live-lock class).
                    log(`reconcile: skipped ${what} for ${hostId}/${source} (HTTP ${status})`, err);
                    if (w.kind === 'action' &&
                        w.action.kind === 'update' &&
                        (status === 404 || status === 410) &&
                        linkMap[w.action.occurrenceKey] !== undefined) {
                        // The mirror itself is gone (manually deleted): drop the link so the
                        // next pass re-creates it instead of re-failing forever. (An
                        // adoption update has no map entry yet; nothing to drop.)
                        delete linkMap[w.action.occurrenceKey];
                        dirty = true;
                        sinceFlush++;
                    }
                    continue;
                }
                // Transient (429 / 5xx / timeout / network / unknown): stop issuing
                // writes for this pass — retrying into a throttling episode only extends
                // the penalty. What was applied is persisted below; the next tick
                // converges the rest. The abort is surfaced on the result AND stamped as
                // the pass's health outcome, so a persistent write outage (e.g. a 403 on
                // every write while reads succeed) never reads as a healthy direction.
                log(`reconcile: apply aborted for ${hostId}/${source} after ${what}`, err);
                abortApply = true;
                abortMessage = `apply aborted after ${what}: ${err instanceof Error ? err.message : String(err)}`;
            }
            finally {
                // Interim flush: bound the link state a crash could lose. Not load
                // bearing (adoption recovers an untracked mirror), just a cost bound.
                // LEASE-GUARDED like the final write: only the lease holder may write
                // the direction doc, so a pass stalled past its TTL (a hung token
                // refresh, say) can never clobber the doc a new holder has since
                // written. The successful re-assert doubles as a heartbeat.
                if (sinceFlush >= LINKS_FLUSH_EVERY && !leaseLost) {
                    const flushTick = deps.now();
                    if (await deps.syncState.renewLease(hostId, source, holder, LEASE_TTL_MS, flushTick)) {
                        lastRenewMs = ms(flushTick);
                        await flushLinks();
                    }
                    else {
                        leaseLost = true;
                    }
                }
            }
        }
        // 8. PERSIST — one final links-doc write when the pass changed anything,
        //    guarded by a lease re-assert: only the lease holder writes the doc, so
        //    a stolen lease aborts the write (the new holder re-derives; adoption
        //    covers any mirror this pass created but could not record).
        if (dirty && sinceFlush > 0 && !leaseLost) {
            if (await deps.syncState.renewLease(hostId, source, holder, LEASE_TTL_MS, deps.now())) {
                await flushLinks();
            }
            else {
                leaseLost = true;
            }
        }
        // Poison skips and a deferred tail are result counts, not pass failures:
        // the pass COMPLETED and advances the success stamp. A transient APPLY
        // ABORT is different — the direction did not converge and the identical
        // failure will recur while the outage lasts — so it stamps the error (the
        // store extends consecutiveFailures) instead of resetting the streak to
        // zero, keeping the health record honest under a persistent write outage.
        outcome = abortApply
            ? {
                lastErrorInstant: deps.now(),
                lastErrorMessage: `reconcile: ${abortMessage ?? 'apply aborted'}`,
            }
            : { lastSuccessInstant: deps.now() };
        return {
            hostId,
            direction,
            ran: true,
            created,
            updated,
            deleted,
            adopted,
            failed,
            deferred,
            ...(abortMessage !== undefined ? { abortedMessage: abortMessage } : {}),
            // The record as read at acquire time: the sweep's subscribe/renew step
            // reads its subscription state off this instead of a separate get.
            record: leaseRecord,
        };
    }
    catch (err) {
        const typed = err instanceof ReconcileError
            ? err
            : new ReconcileError('ReconcileFailed', 'reconcile pass failed', err);
        // Stage-prefixed error stamp ('reconcile: …'), merged into the release
        // write below; the store increments consecutiveFailures off it.
        outcome = {
            lastErrorInstant: deps.now(),
            lastErrorMessage: `reconcile: ${typed.message}`,
        };
        throw typed;
    }
    finally {
        // 9. FINISH — release the lease no matter what (holder-scoped: a no-op
        //    unless we still hold it), merged with the health stamp in ONE write.
        await deps.syncState.finishPass(hostId, source, holder, outcome);
    }
}
//# sourceMappingURL=reconcile.js.map